import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Alert,
  Chip,
  Divider,
} from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import { useAppAnalytics } from "../utils/useAppAnalytics";
import {
  apiService,
  SubscriptionResponse,
  AnnualBilling,
} from "../services/api";
import CardForm, {
  CardFormData,
  emptyCardForm,
  validateCardForm,
  tokenizeCardForm,
} from "./CardForm";

// ─── helpers ────────────────────────────────────────────────────────────────

const formatDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "—";

const planLabel = (sub: SubscriptionResponse): string => {
  if (sub.isLifetime) return "Acesso Vitalício";
  if (sub.planKind === "annual") {
    return sub.annualBilling === "installments_12"
      ? "Anual (12x)"
      : "Anual (à vista)";
  }
  return "Mensal";
};

const statusChip = (sub: SubscriptionResponse) => {
  if (sub.isTrial) return { label: "Em trial", color: "#f09c01" };
  switch (sub.status) {
    case "active":
      return { label: "Ativa", color: "#5BC47A" };
    case "canceled":
      return { label: "Cancelada", color: "#E2A35B" };
    case "failed":
      return { label: "Pagamento pendente", color: "#CC3B2A" };
    default:
      return { label: "Processando", color: "#9aa" };
  }
};

function hasAccess(sub: SubscriptionResponse): boolean {
  if (sub.status === "active") return true;
  if (sub.status === "canceled" && sub.accessUntil) {
    return new Date(sub.accessUntil) > new Date();
  }
  return false;
}

// ─── estilos compartilhados ─────────────────────────────────────────────────

const paperSx = {
  width: "100%",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.1)",
  boxShadow: "none",
  borderRadius: "1.25rem",
  padding: "1.5rem",
  color: "#ffffff",
};

const primaryBtnSx = {
  background: "linear-gradient(135deg, #E2725B 0%, #CC3B2A 100%)",
  color: "#fff",
  borderRadius: "0.875rem",
  textTransform: "none" as const,
  fontWeight: 700,
  "&:disabled": { opacity: 0.5, color: "#fff" },
};

const ghostBtnSx = {
  color: "rgba(255,255,255,0.85)",
  borderColor: "rgba(255,255,255,0.25)",
  borderRadius: "0.875rem",
  textTransform: "none" as const,
};

// ─── componente ─────────────────────────────────────────────────────────────

/**
 * Bloco reutilizável de gestão de assinatura.
 * Busca e exibe dados de assinatura, e oferece ações de atualizar cartão,
 * trocar plano e cancelar — preservando toda a lógica já validada em
 * AccountPage.tsx.
 */
export default function SubscriptionSection() {
  const navigate = useNavigate();
  const { setSubscription, user } = useAuth();
  const { trackEvent } = useAppAnalytics({ user });

  const [sub, setSub] = useState<SubscriptionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [mode, setMode] = useState<"view" | "card" | "changePlan" | "cancel">(
    "view",
  );
  const [card, setCard] = useState<CardFormData>(emptyCardForm);
  const [annualBilling] = useState<AnnualBilling>("upfront");

  // ── carregamento ──────────────────────────────────────────────────────────

  const reload = useCallback(async () => {
    try {
      const data = await apiService.getSubscription();
      setSub(data);
      setSubscription({
        status: data.status as never,
        paymentUrl: null,
        isLifetime: data.isLifetime,
        planKind: data.planKind,
        annualBilling: data.annualBilling,
        isTrial: data.isTrial,
        trialEndsAt: data.trialEndsAt,
        accessUntil: data.accessUntil,
        pendingPlanChange: data.pendingPlanChange,
        cancelScheduledAt: data.cancelScheduledAt,
        cardLastDigits: data.cardLastDigits,
        cardBrand: data.cardBrand,
        commitment: data.commitment,
      });
    } catch {
      setSub(null);
    } finally {
      setLoading(false);
    }
  }, [setSubscription]);

  useEffect(() => {
    reload();
  }, [reload]);

  // ── ações ─────────────────────────────────────────────────────────────────

  const handleUpdateCard = async () => {
    const validationError = validateCardForm(card, false);
    if (validationError) return setError(validationError);
    try {
      setBusy(true);
      setError("");
      const cardToken = await tokenizeCardForm(card);
      await apiService.updateSubscriptionCard({ cardToken });
      setNotice("Cartão atualizado com sucesso.");
      setCard(emptyCardForm);
      setMode("view");
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao atualizar cartão");
    } finally {
      setBusy(false);
    }
  };

  const handleChangePlan = async (
    targetPlanKind: "monthly" | "annual",
    billing?: AnnualBilling,
  ) => {
    try {
      setBusy(true);
      setError("");
      const result = await apiService.changePlan({
        targetPlanKind,
        annualBilling: billing,
      });
      trackEvent("subscription_plan_changed", {
        from_plan: sub?.planKind ?? "unknown",
        to_plan:
          targetPlanKind === "annual" && billing === "installments_12"
            ? "annual_12x"
            : targetPlanKind,
      });
      if (result.prorationCredit) {
        setNotice(
          `Plano alterado para anual. Crédito proporcional aplicado: ${(
            result.prorationCredit / 100
          ).toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
          })}.`,
        );
      } else if (result.effectiveAt) {
        setNotice(
          `Troca agendada. Passa a valer em ${formatDate(result.effectiveAt)}.`,
        );
      } else {
        setNotice("Plano alterado.");
      }
      setMode("view");
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao trocar de plano");
    } finally {
      setBusy(false);
    }
  };

  const handleCancelPendingChange = async () => {
    try {
      setBusy(true);
      setError("");
      const result = await apiService.changePlan({
        targetPlanKind: "monthly",
        cancelPending: true,
      });
      setNotice(result.note || "Troca de plano agendada cancelada.");
      await reload();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao cancelar agendamento",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    try {
      setBusy(true);
      setError("");
      const result = await apiService.cancelSubscription();
      trackEvent("subscription_cancelled", {
        plan_code: sub?.planKind ?? "unknown",
      });
      setNotice(
        result.message ||
          `Assinatura cancelada. Acesso mantido até ${formatDate(
            result.accessUntil,
          )}.`,
      );
      setMode("view");
      await reload();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao cancelar assinatura",
      );
    } finally {
      setBusy(false);
    }
  };

  // ── renderização ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress sx={{ color: "#E2725B" }} />
      </Box>
    );
  }

  if (!sub) {
    return (
      <Paper sx={paperSx}>
        <Typography sx={{ mb: 2 }}>
          Você ainda não tem uma assinatura.
        </Typography>
        <Button sx={primaryBtnSx} onClick={() => navigate("/subscribe")}>
          Ver planos
        </Button>
      </Paper>
    );
  }

  const chip = statusChip(sub);
  const canChangePlan =
    sub.status === "active" && !sub.isTrial && !sub.isLifetime;
  const renewLabel = sub.isTrial
    ? "Fim do trial / 1ª cobrança"
    : "Próxima cobrança";
  const renewDate = sub.isTrial ? sub.trialEndsAt : sub.nextBillingAt;
  // Cancelamento do 12x já agendado (SAL-377): sem este aviso o médico achava
  // que não tinha cancelado e pedia de novo. Com troca de plano pendente, o
  // cancelScheduledAt é do downgrade, que já tem o próprio aviso.
  const cancelScheduledAt =
    sub.status === "active" && !sub.pendingPlanChange
      ? sub.cancelScheduledAt
      : null;
  // Trial correndo: cancelar encerra já, sem cobrança, mesmo no 12x — o
  // compromisso só começa na 1ª cobrança. `isTrial` sozinho não basta: ficou
  // preso em assinaturas antigas depois do fim do trial.
  const inTrial =
    sub.isTrial && !!sub.trialEndsAt && new Date(sub.trialEndsAt) > new Date();
  const inCommitment =
    !!sub.commitment && !inTrial && sub.commitment.paid < sub.commitment.total;

  return (
    <Paper sx={paperSx} data-testid="subscription-section">
      <Typography
        variant="h6"
        sx={{
          fontFamily: "Oxanium, sans-serif",
          fontWeight: 700,
          mb: 2,
        }}
      >
        Assinatura
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {notice}
        </Alert>
      )}

      {sub.status === "failed" && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          data-testid="payment-failed-alert"
        >
          A cobrança da sua assinatura falhou. Atualize o cartão para continuar
          usando o Dr. Weedy.
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
          {planLabel(sub)}
        </Typography>
        <Chip
          label={chip.label}
          sx={{ background: chip.color, color: "#04090a", fontWeight: 700 }}
        />
      </Box>

      {!sub.isLifetime && (
        <Typography sx={{ color: "rgba(255,255,255,0.7)", mb: 0.5 }}>
          {renewLabel}: <strong>{formatDate(renewDate)}</strong>
        </Typography>
      )}

      {sub.commitment && (
        <Typography sx={{ color: "rgba(255,255,255,0.7)", mb: 0.5 }}>
          Compromisso 12x:{" "}
          <strong>
            {sub.commitment.paid}/{sub.commitment.total}
          </strong>{" "}
          parcelas · termina em {formatDate(sub.commitment.endsAt)}
        </Typography>
      )}

      {sub.cardLastDigits && (
        <Typography sx={{ color: "rgba(255,255,255,0.7)", mb: 0.5 }}>
          Cartão: {sub.cardBrand ? `${sub.cardBrand} ` : ""}••••{" "}
          {sub.cardLastDigits}
        </Typography>
      )}

      {sub.status === "canceled" && sub.accessUntil && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Assinatura cancelada. Acesso mantido até {formatDate(sub.accessUntil)}
          .
        </Alert>
      )}

      {sub.pendingPlanChange && (
        <Alert
          severity="info"
          sx={{ mt: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              disabled={busy}
              onClick={handleCancelPendingChange}
            >
              Desfazer
            </Button>
          }
        >
          Troca para o plano mensal agendada para{" "}
          {formatDate(sub.pendingPlanChange.effectiveAt)}.
        </Alert>
      )}

      {cancelScheduledAt && (
        <Alert
          severity="info"
          sx={{ mt: 2 }}
          data-testid="cancel-scheduled-alert"
        >
          Cancelamento agendado para {formatDate(cancelScheduledAt)}. As
          parcelas do compromisso seguem sendo cobradas até lá, o acesso
          continua e a assinatura não renova depois.
        </Alert>
      )}

      <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.1)" }} />

      {/* ── ações ── */}
      {mode === "view" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {(sub.status === "active" || sub.status === "failed") &&
            !sub.isLifetime && (
              <Button
                sx={ghostBtnSx}
                variant="outlined"
                disabled={busy}
                onClick={() => setMode("card")}
              >
                Atualizar cartão
              </Button>
            )}
          {canChangePlan && (
            <Button
              sx={ghostBtnSx}
              variant="outlined"
              disabled={busy}
              onClick={() => setMode("changePlan")}
            >
              Trocar de plano
            </Button>
          )}
          {sub.status === "active" && !sub.isLifetime && !cancelScheduledAt && (
            <Button
              sx={{
                ...ghostBtnSx,
                borderColor: "rgba(204,59,42,0.5)",
                color: "#E2725B",
              }}
              variant="outlined"
              disabled={busy}
              onClick={() => setMode("cancel")}
            >
              Cancelar assinatura
            </Button>
          )}
          {!hasAccess(sub) && (
            <Button
              sx={primaryBtnSx}
              disabled={busy}
              onClick={() => navigate("/subscribe")}
            >
              Reativar assinatura
            </Button>
          )}
        </Box>
      )}

      {mode === "card" && (
        <Box>
          <Typography sx={{ mb: 2, fontWeight: 600 }}>Novo cartão</Typography>
          <CardForm value={card} onChange={setCard} requireBilling={false} />
          <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>
            <Button
              sx={primaryBtnSx}
              disabled={busy}
              onClick={handleUpdateCard}
            >
              {busy ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Salvar cartão"
              )}
            </Button>
            <Button
              sx={ghostBtnSx}
              variant="outlined"
              disabled={busy}
              onClick={() => setMode("view")}
            >
              Cancelar
            </Button>
          </Box>
        </Box>
      )}

      {mode === "changePlan" && (
        <Box>
          {sub.planKind === "monthly" ? (
            <>
              <Typography sx={{ mb: 1, fontWeight: 600 }}>
                Migrar para o plano Anual
              </Typography>
              <Typography
                sx={{ color: "rgba(255,255,255,0.7)", mb: 2, fontSize: 14 }}
              >
                A troca é imediata e o tempo não usado do mês atual vira crédito
                na 1ª cobrança do anual.
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <Button
                  sx={primaryBtnSx}
                  disabled={busy}
                  onClick={() => handleChangePlan("annual", "upfront")}
                >
                  Anual à vista
                </Button>
                <Button
                  sx={primaryBtnSx}
                  disabled={busy}
                  onClick={() => handleChangePlan("annual", "installments_12")}
                >
                  Anual em 12x
                </Button>
              </Box>
              <Typography
                sx={{ color: "rgba(255,255,255,0.5)", mt: 1.5, fontSize: 12 }}
              >
                No anual em 12x, ao cancelar as parcelas restantes seguem sendo
                cobradas até completar 12.
              </Typography>
            </>
          ) : (
            <>
              <Typography sx={{ mb: 1, fontWeight: 600 }}>
                Migrar para o plano Mensal
              </Typography>
              <Typography
                sx={{ color: "rgba(255,255,255,0.7)", mb: 2, fontSize: 14 }}
              >
                A troca passa a valer no fim do período já pago
                {sub.annualBilling === "installments_12"
                  ? " (fim do compromisso de 12 meses)"
                  : ""}
                . Sem reembolso.
              </Typography>
              <Button
                sx={primaryBtnSx}
                disabled={busy}
                onClick={() => handleChangePlan("monthly")}
              >
                Agendar troca para mensal
              </Button>
            </>
          )}
          <Box sx={{ mt: 2 }}>
            <Button
              sx={ghostBtnSx}
              variant="outlined"
              disabled={busy}
              onClick={() => setMode("view")}
            >
              Voltar
            </Button>
          </Box>
        </Box>
      )}

      {mode === "cancel" && (
        <Box>
          <Typography sx={{ mb: 1, fontWeight: 600 }}>
            Cancelar assinatura
          </Typography>
          <Typography
            sx={{ color: "rgba(255,255,255,0.7)", mb: 2, fontSize: 14 }}
          >
            {inTrial
              ? `Você está no período de teste: a assinatura é encerrada agora e nada será cobrado. O acesso continua até ${formatDate(sub.trialEndsAt)}.`
              : inCommitment
                ? "Você tem um compromisso de 12 meses. As parcelas restantes seguirão sendo cobradas até o fim do compromisso; o acesso é mantido até lá e a assinatura não renova depois."
                : "Você manterá o acesso até o fim do período já pago. Sem reembolso."}
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              sx={{
                ...primaryBtnSx,
                background: "linear-gradient(135deg, #CC3B2A 0%, #8B1E12 100%)",
              }}
              disabled={busy}
              onClick={handleCancel}
            >
              {busy ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Confirmar cancelamento"
              )}
            </Button>
            <Button
              sx={ghostBtnSx}
              variant="outlined"
              disabled={busy}
              onClick={() => setMode("view")}
            >
              Voltar
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
}
